import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
    Alert,
    Autocomplete,
    Box,
    Breadcrumbs,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Grid,
    LinearProgress,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Category as CategoryIcon,
    CheckCircle as CheckIcon,
    HighlightOff as RejectIcon,
    Undo as UndoIcon,
} from '@mui/icons-material';
import { getProject, getThemeReview, saveThemeReviewDecision } from '../services/api';

const STATUS_OPTIONS = [
    { value: 'pending', label: 'Pending', color: 'warning' },
    { value: 'accepted', label: 'Accepted', color: 'success' },
    { value: 'rejected', label: 'Rejected', color: 'error' },
    { value: 'all', label: 'All', color: 'default' },
];

const THEME_OPTIONS = [
    'Absence',
    'Disability',
    'Appeals',
    'Accommodations ADA',
    'Payments and Benefits',
    'Customers',
    'Employee',
    'Combined',
    'Other',
];

const STATUS_COLOR = {
    pending: 'warning',
    accepted: 'success',
    rejected: 'error',
};

function ThemeReviewPage() {
    const { projectId, sourceSystem, fileId } = useParams();
    const navigate = useNavigate();

    const [project, setProject] = useState(null);
    const [reviewData, setReviewData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [statusFilter, setStatusFilter] = useState('all');
    const [tableFilter, setTableFilter] = useState('all');
    const [themeFilter, setThemeFilter] = useState('all');
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(20);
    const [selected, setSelected] = useState(null);
    const [draftTheme, setDraftTheme] = useState('');

    useEffect(() => {
        const loadProject = async () => {
            try {
                setProject(await getProject(projectId));
            } catch (err) {
                console.error('Error loading project:', err);
            }
        };
        loadProject();
    }, [projectId]);

    const fetchReviewData = async () => {
        try {
            setLoading(true);
            const data = await getThemeReview(projectId, sourceSystem, fileId, {
                page: page + 1,
                pageSize,
                statusFilter,
                tableFilter,
                themeFilter,
            });
            setReviewData(data);
            setError(null);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to load theme review data.');
            console.error('Error loading theme review data:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReviewData();
    }, [projectId, sourceSystem, fileId, statusFilter, tableFilter, themeFilter, page, pageSize]);

    useEffect(() => {
        if (!reviewData?.tables_data?.length) {
            setSelected(null);
            return;
        }
        if (!selected) {
            setSelected(reviewData.tables_data[0]);
            return;
        }
        const match = reviewData.tables_data.find((item) => item.table_key === selected.table_key);
        setSelected(match || reviewData.tables_data[0]);
    }, [reviewData]);

    useEffect(() => {
        setDraftTheme(selected?.theme || selected?.theme_generated_value || '');
    }, [selected]);

    const counts = reviewData?.counts || {};
    const totalGenerated = reviewData?.total_generated || 0;
    const reviewedCount = (counts.accepted || 0) + (counts.rejected || 0);
    const progressValue = totalGenerated ? Math.round((reviewedCount / totalGenerated) * 100) : 0;
    const isDirty = selected ? draftTheme !== (selected.theme || '') : false;

    const handleDecision = async (status) => {
        if (!selected) {
            return;
        }
        if (!draftTheme) {
            setError('Choose a theme before saving.');
            return;
        }
        try {
            setSaving(true);
            await saveThemeReviewDecision({
                project_id: projectId,
                source_system: sourceSystem,
                file_id: fileId,
                table_name: selected.table_name,
                status,
                theme: draftTheme,
            });
            await fetchReviewData();
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to save theme review decision.');
            console.error('Error saving theme review decision:', err);
        } finally {
            setSaving(false);
        }
    };

    const handleResetFilters = () => {
        setStatusFilter('all');
        setTableFilter('all');
        setThemeFilter('all');
        setPage(0);
    };

    const tableOptions = useMemo(() => reviewData?.tables || [], [reviewData]);
    const themeOptions = useMemo(() => reviewData?.themes || [], [reviewData]);

    if (loading && !reviewData) {
        return (
            <Box>
                <LinearProgress sx={{ mb: 2 }} />
                <Card>
                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <CircularProgress size={20} />
                        <Typography>Loading generated themes...</Typography>
                    </CardContent>
                </Card>
            </Box>
        );
    }

    return (
        <Box>
            <Breadcrumbs sx={{ mb: 2 }}>
                <Link to="/schema-analyzer/projects" style={{ textDecoration: 'none', color: 'inherit' }}>
                    <Typography color="text.secondary" sx={{ '&:hover': { color: 'primary.main' } }}>
                        Projects
                    </Typography>
                </Link>
                <Link to={`/schema-analyzer/projects/${projectId}/upload`} style={{ textDecoration: 'none', color: 'inherit' }}>
                    <Typography color="text.secondary" sx={{ '&:hover': { color: 'primary.main' } }}>
                        {project?.name || 'Project'}
                    </Typography>
                </Link>
                <Typography color="text.primary">Theme Review</Typography>
            </Breadcrumbs>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/schema-analyzer/projects/${projectId}/upload`)} variant="outlined">
                        Back
                    </Button>
                    <Box>
                        <Typography variant="h4" fontWeight={700}>
                            Generated Theme Review
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {reviewData?.file_name || 'File'} | {sourceSystem}
                        </Typography>
                    </Box>
                </Box>
                <Chip
                    icon={<CategoryIcon />}
                    label={`${progressValue}% reviewed`}
                    color={progressValue === 100 ? 'success' : 'primary'}
                    variant="outlined"
                />
            </Box>

            {loading && reviewData && <LinearProgress sx={{ mb: 2 }} />}
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            <Card sx={{ mb: 3, background: 'linear-gradient(135deg, rgba(0,119,200,0.08), rgba(0,166,81,0.08))' }}>
                <CardContent>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={4}>
                            <Typography variant="overline" color="text.secondary">Review Progress</Typography>
                            <Typography variant="h4" fontWeight={700}>{reviewedCount} / {totalGenerated}</Typography>
                            <Typography variant="body2" color="text.secondary">Accepted or rejected themes</Typography>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <LinearProgress variant="determinate" value={progressValue} sx={{ height: 10, borderRadius: 6, mb: 1 }} />
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {STATUS_OPTIONS.filter((option) => option.value !== 'all').map((option) => (
                                    <Chip
                                        key={option.value}
                                        label={`${option.label}: ${counts[option.value] || 0}`}
                                        color={option.color}
                                        variant="outlined"
                                        size="small"
                                    />
                                ))}
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            <Grid container spacing={3}>
                <Grid item xs={12} lg={7}>
                    <Card sx={{ mb: 2 }}>
                        <CardContent>
                            <Typography variant="h6" fontWeight={600} gutterBottom>Filter & Search</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={4}>
                                    <TextField select label="Status" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} fullWidth>
                                        {STATUS_OPTIONS.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Autocomplete
                                        freeSolo
                                        options={tableOptions}
                                        value={tableFilter === 'all' ? null : tableFilter}
                                        inputValue={tableFilter === 'all' ? '' : tableFilter}
                                        onInputChange={(_, value) => setTableFilter(value || 'all')}
                                        onChange={(_, value) => setTableFilter(value || 'all')}
                                        renderInput={(params) => <TextField {...params} label="Table" placeholder="Search tables" />}
                                        clearOnEscape
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Autocomplete
                                        freeSolo
                                        options={themeOptions}
                                        value={themeFilter === 'all' ? null : themeFilter}
                                        inputValue={themeFilter === 'all' ? '' : themeFilter}
                                        onInputChange={(_, value) => setThemeFilter(value || 'all')}
                                        onChange={(_, value) => setThemeFilter(value || 'all')}
                                        renderInput={(params) => <TextField {...params} label="Theme" placeholder="Search themes" />}
                                        clearOnEscape
                                        fullWidth
                                    />
                                </Grid>
                            </Grid>
                            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                                <Button variant="outlined" onClick={handleResetFilters}>Reset</Button>
                            </Box>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent sx={{ pb: 0 }}>
                            <Typography variant="h6" fontWeight={600} gutterBottom>
                                Generated Themes ({reviewData?.pagination?.total_items || 0})
                            </Typography>
                        </CardContent>
                        <TableContainer sx={{ maxHeight: 520 }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Table</TableCell>
                                        <TableCell>LLM Suggested</TableCell>
                                        <TableCell>Final Theme</TableCell>
                                        <TableCell>Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(reviewData?.tables_data || []).map((item) => (
                                        <TableRow
                                            key={item.table_key}
                                            hover
                                            selected={selected?.table_key === item.table_key}
                                            onClick={() => setSelected(item)}
                                            sx={{ cursor: 'pointer' }}
                                        >
                                            <TableCell>{item.table_name}</TableCell>
                                            <TableCell>
                                                <Chip label={item.theme_generated_value || 'None'} size="small" variant="outlined" />
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={item.theme || 'None'} size="small" color="primary" variant="outlined" />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={item.theme_review_status}
                                                    size="small"
                                                    color={STATUS_COLOR[item.theme_review_status] || 'default'}
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {!reviewData?.tables_data?.length && (
                                        <TableRow>
                                            <TableCell colSpan={4}>
                                                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                                    No generated themes are available for review.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="caption" color="text.secondary">
                                Page {reviewData?.pagination?.page || 1} of {reviewData?.pagination?.total_pages || 1}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <TextField
                                    select
                                    size="small"
                                    label="Rows"
                                    value={pageSize}
                                    onChange={(event) => {
                                        setPageSize(Number(event.target.value));
                                        setPage(0);
                                    }}
                                >
                                    {[10, 20, 30, 50, 100].map((size) => (
                                        <MenuItem key={size} value={size}>
                                            {size}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    disabled={(reviewData?.pagination?.page || 1) <= 1}
                                    onClick={() => setPage((prev) => Math.max(prev - 1, 0))}
                                >
                                    Prev
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    disabled={(reviewData?.pagination?.page || 1) >= (reviewData?.pagination?.total_pages || 1)}
                                    onClick={() => setPage((prev) => prev + 1)}
                                >
                                    Next
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} lg={5}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" fontWeight={600} gutterBottom>Selected Table</Typography>
                            {!selected && (
                                <Typography variant="body2" color="text.secondary">Choose a table to review its theme.</Typography>
                            )}
                            {selected && (
                                <>
                                    <Typography variant="subtitle1" fontWeight={700}>{selected.table_name}</Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        {selected.table_description || 'No table description available.'}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                                        <Chip label={`Columns: ${selected.column_count || 0}`} size="small" variant="outlined" />
                                        <Chip label={`Final: ${selected.theme || 'None'}`} size="small" color="primary" variant="outlined" />
                                        <Chip label={`LLM Suggested: ${selected.theme_generated_value || 'None'}`} size="small" color="secondary" variant="outlined" />
                                        <Chip
                                            label={`Status: ${selected.theme_review_status}`}
                                            size="small"
                                            color={STATUS_COLOR[selected.theme_review_status] || 'default'}
                                            variant="outlined"
                                        />
                                    </Box>
                                    <TextField
                                        select
                                        fullWidth
                                        label="Theme"
                                        value={draftTheme}
                                        onChange={(event) => setDraftTheme(event.target.value)}
                                        sx={{ mb: 2 }}
                                    >
                                        {THEME_OPTIONS.map((theme) => (
                                            <MenuItem key={theme} value={theme}>{theme}</MenuItem>
                                        ))}
                                    </TextField>
                                    <Typography variant="subtitle2" color="text.secondary">Model reasoning</Typography>
                                    <Typography variant="body2" sx={{ mb: 3 }}>
                                        {selected.theme_generated_reasoning || 'No reasoning available.'}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        <Button
                                            variant="contained"
                                            startIcon={<CheckIcon />}
                                            disabled={!selected || !draftTheme || saving}
                                            onClick={() => handleDecision('accepted')}
                                        >
                                            Accept
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            startIcon={<RejectIcon />}
                                            disabled={!selected || !draftTheme || saving}
                                            onClick={() => handleDecision('rejected')}
                                        >
                                            Reject
                                        </Button>
                                        <Button
                                            variant="text"
                                            startIcon={<UndoIcon />}
                                            disabled={!selected || (!isDirty && selected.theme_review_status === 'pending') || saving}
                                            onClick={() => handleDecision('pending')}
                                        >
                                            Reset Pending
                                        </Button>
                                    </Box>
                                </>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
}

export default ThemeReviewPage;
